import { useState } from 'react';
import { Button } from '@/components/ui/button-comp';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCookie } from '@ekonavi/auth';
import { CheckCircle, XCircle, Clock, AlertCircle, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiEndpoint } from '@/config';

interface Methodology {
	id: string;
	name: string;
	description: string;
	status: string;
	created_at: string;
	creator?: {
		name: string;
	};
}

interface MethodologyApprovalProps {
	methodologies: Methodology[];
	onUpdate: () => void;
}

export function MethodologyApproval({ methodologies, onUpdate }: MethodologyApprovalProps) {
	const authToken = getCookie('auth_token');
	const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : undefined;
	const [isLoading, setIsLoading] = useState(false);
	const [expandedMethodology, setExpandedMethodology] = useState<string | null>(null);
	const [adminNotes, setAdminNotes] = useState<{ [key: string]: string }>({});

	const handleApprove = async (methodologyId: string) => {
		if (!authHeaders) return;

		setIsLoading(true);
		try {
			const response = await fetch(`${apiEndpoint}/methodologies/${methodologyId}`, {
				method: 'PUT',
				headers: {
					...authHeaders,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					status: 'approved',
					admin_notes: adminNotes[methodologyId] || 'Aprovado pelo administrador'
				})
			});

			if (response.ok) {
				toast.success('Metodologia aprovada com sucesso!');
				onUpdate();
			} else {
				toast.error('Erro ao aprovar metodologia');
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error approving methodology:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleReject = async (methodologyId: string) => {
		if (!authHeaders) return;

		setIsLoading(true);
		try {
			const response = await fetch(`${apiEndpoint}/methodologies/${methodologyId}`, {
				method: 'PUT',
				headers: {
					...authHeaders,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					status: 'rejected',
					admin_notes: adminNotes[methodologyId] || 'Rejeitado pelo administrador'
				})
			});

			if (response.ok) {
				toast.success('Metodologia rejeitada');
				onUpdate();
			} else {
				toast.error('Erro ao rejeitar metodologia');
			}
		} catch (error) {
			toast.error('Erro de conexão com a API');
			console.error('Error rejecting methodology:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case 'pending':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
			case 'approved':
				return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
			case 'rejected':
				return <Badge variant="outline" className="text-red-600 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	if (methodologies.length === 0) {
		return (
			<div className="text-center py-8">
				<AlertCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
				<p className="text-muted-foreground">Nenhuma metodologia encontrada</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Nome</TableHead>
						<TableHead>Criador</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Data</TableHead>
						<TableHead>Ações</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{methodologies.map((methodology) => (
						<>
							<TableRow key={methodology.id}>
								<TableCell>
									<div>
										<p className="font-medium">{methodology.name}</p>
										<p className="text-sm text-muted-foreground line-clamp-2">
											{methodology.description}
										</p>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<User className="w-4 h-4" />
										{methodology.creator?.name || 'Não informado'}
									</div>
								</TableCell>
								<TableCell>{getStatusBadge(methodology.status)}</TableCell>
								<TableCell>
									{new Date(methodology.created_at).toLocaleDateString('pt-BR')}
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setExpandedMethodology(
												expandedMethodology === methodology.id ? null : methodology.id
											)}
										>
											{expandedMethodology === methodology.id ? 'Ocultar' : 'Detalhes'}
										</Button>
										{methodology.status === 'pending' && (
											<>
												<Button
													variant="outline"
													size="sm"
													className="text-green-600 border-green-300 hover:bg-green-50"
													onClick={() => handleApprove(methodology.id)}
													disabled={isLoading}
												>
													<CheckCircle className="w-4 h-4 mr-1" />
													Aprovar
												</Button>
												<Button
													variant="outline"
													size="sm"
													className="text-red-600 border-red-300 hover:bg-red-50"
													onClick={() => handleReject(methodology.id)}
													disabled={isLoading}
												>
													<XCircle className="w-4 h-4 mr-1" />
													Rejeitar
												</Button>
											</>
										)}
									</div>
								</TableCell>
							</TableRow>
							{expandedMethodology === methodology.id && (
								<TableRow>
									<TableCell colSpan={5}>
										<div className="p-4 bg-muted/30 rounded-lg space-y-4">
											<div>
												<h4 className="font-medium mb-2">Descrição Completa</h4>
												<p className="text-sm text-muted-foreground">
													{methodology.description}
												</p>
											</div>
											<div>
												<h4 className="font-medium mb-2">Detalhes Técnicos</h4>
												<div className="grid grid-cols-2 gap-4 text-sm">
													<div>
														<span className="font-medium">ID:</span> {methodology.id}
													</div>
													<div>
														<span className="font-medium">Status:</span> {methodology.status}
													</div>
												</div>
											</div>
											{methodology.status === 'pending' && (
												<div className="space-y-2">
													<Label htmlFor={`notes-${methodology.id}`}>
														Observações do Administrador
													</Label>
													<Input
														id={`notes-${methodology.id}`}
														value={adminNotes[methodology.id] || ''}
														onChange={(e) => setAdminNotes(prev => ({
															...prev,
															[methodology.id]: e.target.value
														}))}
														placeholder="Adicione observações sobre a decisão..."
													/>
												</div>
											)}
										</div>
									</TableCell>
								</TableRow>
							)}
						</>
					))}
				</TableBody>
			</Table>
		</div>
	);
}